import { v4 as uuidv4 } from 'uuid';
import { createClient } from '@supabase/supabase-js';
import bcrypt from 'bcryptjs';

// Supabase client initialization
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  import.meta.env.LOCALHOST_URL
);

// Supabase admin client with service role
const supabaseAdmin = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY
);

// Email configuration
const emailConfig = {
  host: 'mail.beetrax.net',
  port: 587,
  secure: false,
  auth: {
    user: import.meta.env.VITE_EMAIL_USER,
    pass: import.meta.env.VITE_EMAIL_PASSWORD
  }
};

export const passwordResetService = {
  // Generate reset token and save to database
  async generateResetToken(email) {
    try {
      // Check if user exists in user_profiles
      const { data: userData, error: userError } = await supabase
        .from('user_profiles')
        .select('id, email, first_name')
        .eq('email', email);
        // Removed .single()

      // Check if the user was found (data is not null and has at least one item)
      if (!userData || userData.length === 0) {
        // User not found - return a friendly error without logging to console
        throw new Error('Bu email adresi ile kayıtlı bir kullanıcı bulunamadı.');
      }

      // User found - use the first item in the array
      const user = userData[0];

      // Check for existing active reset tokens (plural)
      const { data: existingTokens, error: fetchError } = await supabase
        .from('password_resets')
        .select('id') // Only select the id, as that's all we need
        .eq('user_id', user.id)
        .eq('used', false)
        .gt('expires_at', new Date().toISOString());
        // Removed .single()

      if (fetchError) {
          console.error('Error fetching existing tokens:', fetchError);
          // Decide if you want to throw an error here or just log it and continue
      }

      // Invalidate all existing active tokens if any are found
      if (existingTokens && existingTokens.length > 0) {
        const tokenIds = existingTokens.map(token => token.id);
        console.log(`Invalidating ${tokenIds.length} existing token(s) for user ${user.id}`);
        const { error: updateError } = await supabase
          .from('password_resets')
          .update({ used: true })
          .in('id', tokenIds); // Use .in() to update multiple rows

        if (updateError) {
            console.error('Error invalidating existing tokens:', updateError);
            // Decide if you want to throw an error here or just log it and continue
        }
      }

      // Generate reset token and set expiry
      const resetToken = uuidv4();
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 1); // 1 saat geçerli

      // Save reset token to password_resets table
      const { error: tokenError } = await supabase
        .from('password_resets')
        .insert({
          user_id: user.id,
          token: resetToken,
          expires_at: expiresAt.toISOString(),
          used: false
        });

      if (tokenError) {
        console.error('Token save error:', tokenError);
        throw new Error('Şifre sıfırlama işlemi başlatılamadı. Lütfen tekrar deneyin.');
      }

      // Send custom email with reset link
      const resetLink = `${window.location.origin}/reset-password?token=${resetToken}`;
      const emailHtml = `
        <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0"
          />
          <style>
            @keyframes fadeIn {
              0% {
                opacity: 0;
                transform: translateY(8px);
              }
              100% {
                opacity: 1;
                transform: translateY(0);
              }
            }
          </style>
        </head>
        <body
          style="
            margin: 0;
            padding: 0;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            background: linear-gradient(
              to right,
              #ffffff,
              #ffffff
            );
          "
        >
          <div
            style="
              max-width: 600px;
              margin: 40px auto;
              border-radius: 16px;
              overflow: hidden;
              background: rgba(255, 255, 255, 0.25);
              backdrop-filter: blur(12px);
              -webkit-backdrop-filter: blur(12px);
              border: 1px solid rgba(255, 255, 255, 0.3);
              box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
              animation: fadeIn 0.6s ease-in-out;
            "
          >
            <!-- Header -->
            <div
              style="
                background: linear-gradient(90deg, #1e293b, #334155);
                padding: 32px 40px;
                text-align: left;
                background-clip: padding-box;
              "
            >
              <h1
                style="
                  color: #ffffff;
                  margin: 0;
                  font-size: 28px;
                  font-weight: 600;
                  letter-spacing: -0.5px;
                "
              >
                Beetrax.net - Şifre Sıfırlama
              </h1>
              <p
                style="
                  color: #94a3b8;
                  margin: 8px 0 0 0;
                  font-size: 16px;
                  font-weight: 400;
                "
              >
                Güvenlik Doğrulama
              </p>
            </div>

            <!-- İçerik -->
            <div style="padding: 40px;">
              <p
                style="
                  color: #1e293b;
                  font-size: 16px;
                  margin-bottom: 24px;
                  font-weight: 500;
                "
              >
                Merhaba ${user.first_name || 'Değerli Kullanıcımız'},
              </p>

              <p
                style="
                  color: #475569;
                  font-size: 16px;
                  margin-bottom: 24px;
                  line-height: 1.7;
                "
              >
                Beetrax.net hesabınız için bir şifre sıfırlama talebi aldık. Güvenliğiniz için
                bu işlemi siz başlattıysanız, aşağıdaki butonu kullanarak
                şifrenizi sıfırlayabilirsiniz.
              </p>

              <!-- Buton -->
              <div style="margin: 32px 0; text-align: left;">
                <a
                  href="${resetLink}"
                  style="
                    display: inline-block;
                    text-decoration: none;
                    padding: 16px 32px;
                    border-radius: 8px;
                    font-weight: 500;
                    font-size: 16px;
                    color: #ffffff;
                    background: linear-gradient(90deg, #1e293b, #475569);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                    transition: background 0.3s ease;
                  "
                  onmouseover="this.style.background='linear-gradient(90deg,#26303f,#54607a)'"
                  onmouseout="this.style.background='linear-gradient(90deg,#1e293b,#475569)'"
                >
                  Şifremi Sıfırla
                </a>
              </div>

              <!-- Güvenlik Bilgisi -->
              <div
                style="
                  border-left: 4px solid #f59e0b;
                  background-color: #fffbeb;
                  border-radius: 4px;
                  padding: 20px;
                  margin: 32px 0;
                "
              >
                <p
                  style="
                    color: #92400e;
                    font-size: 14px;
                    margin: 0 0 8px 0;
                    font-weight: 600;
                  "
                >
                  Güvenlik Bildirimi
                </p>
                <ul
                  style="
                    color: #92400e;
                    font-size: 14px;
                    margin: 12px 0 0 0;
                    padding-left: 20px;
                  "
                >
                  <li style="margin-bottom: 8px;">
                    Bu bağlantı <strong>1 saat</strong> süreyle geçerlidir
                  </li>
                  <li style="margin-bottom: 8px;">
                    Bu talebi siz oluşturmadıysanız, lütfen dikkate almayın
                  </li>
                  <li>
                    Şüpheli bir durum fark ederseniz hemen Beetrax.net destek ekibine bildirin
                  </li>
                </ul>
              </div>

              <hr
                style="
                  border: none;
                  border-top: 1px solid #e2e8f0;
                  margin: 32px 0;
                "
              />

              <p
                style="
                  color: #475569;
                  font-size: 16px;
                  margin: 24px 0;
                  line-height: 1.7;
                "
              >
                Güvenliğiniz bizim için önemli. Herhangi bir sorunuz olursa, Beetrax.net
                destek ekibimizle iletişime geçebilirsiniz.
              </p>

              <p
                style="
                  color: #1e293b;
                  font-size: 16px;
                  margin: 32px 0 0 0;
                "
              >
                Saygılarımızla,<br />
                <strong style="color: #1e293b;">Beetrax.net Ekibi</strong>
              </p>
            </div>

            <!-- Footer -->
            <div
              style="
                background-color: rgba(248, 250, 252, 0.7);
                padding: 24px 40px;
                text-align: left;
                border-top: 1px solid rgba(255, 255, 255, 0.3);
              "
            >
              <p style="color: #64748b; font-size: 14px; margin: 0;">
                Bu e-posta Beetrax.net tarafından otomatik olarak gönderilmiştir, lütfen yanıtlamayınız.
              </p>
              <div
                style="
                  margin-top: 16px;
                  color: #64748b;
                  font-size: 14px;
                "
              >
                © ${new Date().getFullYear()} Beetrax.net. Tüm hakları saklıdır.
              </div>
            </div>
          </div>
        </body>
      </html>
      `;

      // Send email using your email service
      try {
        const response = await fetch(`${import.meta.env.VITE_LOCALHOST_URL}/api/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            to: email,
            subject: 'Şifre Sıfırlama İsteği',
            html: emailHtml
          })
        });

        // İlk olarak response'un ok olup olmadığını kontrol et
        if (!response.ok) {
          // Response text'i al
          const errorText = await response.text();
          let errorMessage;
          
          try {
            // JSON parse etmeyi dene
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || 'Email gönderilemedi';
          } catch (e) {
            // JSON parse edilemezse text'i kullan
            errorMessage = errorText || `Email gönderilemedi (${response.status})`;
          }
          
          throw new Error(errorMessage);
        }

        // Response OK ise JSON'ı parse et
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.message || 'Email gönderilemedi');
        }

        return true;
      } catch (error) {
        if (error.message.includes('Failed to fetch')) {
          throw new Error('Email servisi çalışmıyor. Lütfen sistem yöneticinize başvurun.');
        }
        throw error;
      }
    } catch (error) {
      // Only log errors that are not "user not found" errors
      if (error.message !== 'Bu email adresi ile kayıtlı bir kullanıcı bulunamadı.') {
        console.error('Password reset error:', error);
      }
      throw error;
    }
  },

  // Verify reset token
  async verifyResetToken(token) {
    try {
      const { data, error } = await supabase
        .from('password_resets')
        // Select only necessary fields from password_resets
        .select('id, user_id, expires_at') 
        .eq('token', token)
        .eq('used', false)
        .gt('expires_at', new Date().toISOString())
        // Removed .single() - we'll check the length of the result array

      // Check for Supabase errors or if no valid token was found
      if (error) {
        console.error('Supabase error verifying token:', error);
        throw new Error('Token doğrulama sırasında bir veritabanı hatası oluştu.');
      }
      
      // Check if exactly one valid token was found
      if (!data || data.length !== 1) {
        console.warn('Token verification failed: No valid token found or multiple tokens found.', data);
        throw new Error('Geçersiz veya süresi dolmuş token.'); // Changed error message slightly
      }

      // Return the first (and only) valid token data object
      return data[0]; 
    } catch (error) {
      // Catch errors thrown above or other unexpected errors
      console.error('Token verification error:', error.message);
      // Re-throw the specific error message or a generic one
      throw new Error(error.message || 'Token doğrulanamadı.'); 
    }
  },

  // Reset password
  async resetPassword(token, newPassword) {
    try {
      // Verify the token first, this now returns { id, user_id, expires_at }
      const resetData = await this.verifyResetToken(token); 
      
      // Fetch user profile data separately using the user_id from resetData
      const { data: userData, error: userError } = await supabase
          .from('user_profiles')
          .select('id, email')
          .eq('id', resetData.user_id)
          .single();

      if (userError || !userData) {
          console.error('Error fetching user profile for password reset:', userError);
          throw new Error('Şifre sıfırlama için kullanıcı bilgileri bulunamadı.');
      }

      // Generate new password hash
      const salt = await bcrypt.genSalt(10);
      const hashedPassword = await bcrypt.hash(newPassword, salt);

      // Update password hash in user_profiles
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ 
          password_hash: hashedPassword,
          updated_at: new Date().toISOString()
        })
        .eq('id', userData.id);

      if (updateError) {
        throw new Error('Şifre güncellenemedi. Lütfen tekrar deneyin.');
      }

      // Update the password in Supabase auth.users using the admin client
      try {
        // Use the admin API to update the user's password
        const { error: updateAuthError } = await supabaseAdmin.auth.admin.updateUserById(
          userData.id,
          { password: newPassword }
        );

        if (updateAuthError) {
          console.error('Error updating auth user:', updateAuthError);
          throw new Error('Kimlik doğrulama şifresi güncellenemedi.');
        }
      } catch (authError) {
        console.error('Auth password update error:', authError);
        throw new Error('Kimlik doğrulama şifresi güncellenemedi. Lütfen tekrar deneyin.');
      }

      // Mark token as used
      await supabase
        .from('password_resets')
        .update({ used: true })
        .eq('token', token);

      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }
}; 